import React, {ReactNode, forwardRef, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import {
  ApplyPageStyleError,
  AssetType,
  Common,
  CoreDoms,
  DetectionType,
  EditorExperienceType,
  FlowDirection,
  ItemState,
  LayoutContext,
  PageAPI,
  PageConstants,
  PageDetectionActionType,
  ScrapBookDropMode,
  ViewMode,
  Viewers,
} from '@taopix/taopix-core';
import {CommandsList, PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import classNames from 'classnames';
import {DialogStatus, DragAndDropNames} from '../../Enums';
import useViewMode from '../../Hooks/useViewMode';
import {getDetectionType} from '../../Utils';
import {DragAndDropState} from '../Context/DragAndDrop/DragAndDropContext';
import {useTaopix} from '../Context/TaopixContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../Experience/ExperienceProvider';
import Drop from '../DragAndDrop/Drop';
import './page.scss';
import {useRegisterKeyboardCommand} from '../Context/KeyboardCommandsContext';

export type PageProps = {
  page: PageInterface;
  layout: ProductLayout;
  editorType: EditorExperienceType;
  viewMode?: ViewMode;
  gridCellMaxWidth?: number;
  gridCellMaxHeight?: number;
  gridRowGap?: number;
  gridColGap?: number;
  gridCellPadding?: number;
  showEmptyBoxes?: boolean;
  showErrors?: boolean;
  showBleed?: {
    outside: boolean;
    inside: boolean;
  };
  showPageHighlights?: boolean;
  children?: ReactNode[] | ReactNode;
  className?: string;
  cache?: boolean;
  layoutContext?: LayoutContext;
  uiLayoutMode?: 'auto' | 'basic'; // Used to determine whether to apply layouts styles to the page container
};

const Page = forwardRef(
  (
    {
      page,
      layout,
      editorType,
      viewMode = ViewMode.List,
      gridCellMaxWidth,
      gridCellMaxHeight,
      gridRowGap,
      gridColGap,
      gridCellPadding,
      showEmptyBoxes = true,
      showErrors = true,
      showBleed = {
        outside: true,
        inside: true,
      },
      showPageHighlights = true,
      children,
      className,
      cache = true,
      layoutContext = LayoutContext.PageAPI,
      uiLayoutMode = 'auto',
    }: PageProps,
    fwdRef
  ) => {
    const app = useTaopix();
    const ui = useUiState();
    const {experience} = useExperience();
    const [storeViewMode] = useViewMode();
    const ref = useRef<PageAPI | null>(null);
    const pageContainerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLDivElement>(null);

    const currentPageNumber = useRef(ui.state.pageNumber);

    useEffect(() => {
      currentPageNumber.current = ui.state.pageNumber;
    }, [ui.state.pageNumber]);

    useRegisterKeyboardCommand({
      code: 'Digit3',
      shiftKey: true,
      callback: () => {
        if (Number(pageContainerRef.current?.id.split('-')[1]) === currentPageNumber.current) {
          pageContainerRef?.current?.focus();
        }
      },
    });

    // if a fowardRef is passed in, yield it
    useImperativeHandle(fwdRef, () => ref.current);

    const id = `${CoreDoms.ViewportName}-${Viewers.Primary}-${page.pagenumber}`;

    const settings = useMemo(() => {
      const config = {
        viewerName: Viewers.Primary,
        ismobile: false,
        isCanvasListening: true,
        isCalendarEvent: false,
        singlePageThumbnail: false,
        autoLayoutEnabled: true,
        zoom: 100,
        pageNumber: page.pagenumber,
        cache: cache,
        editorType,
        components: {
          background: true,
          bleed: showBleed,
          gutter: true,
          spine: true,
          flaps: true,
          empty: showEmptyBoxes,
          wrap: true,
          errors: showErrors,
        },
      };
      if (layoutContext) {
        config['layoutContext'] = layoutContext;
      }

      return config;
    }, [viewMode]);

    React.useEffect(() => {
      let api: PageAPI = null as any;
      if (!ref.current) {
        api = new PageAPI(settings);
        ref.current = api;
      }

      api
        .setSpreadSize({
          pageNumber: page.pagenumber,
          rulersVisible: false,
          rulerOriginHeight: 0,
          borderSize: 0,
          left: 0,
          top: 0,
          gridCellMaxWidth,
          gridCellMaxHeight,
          gridRowGap,
          gridColGap,
          gridCellPadding,
          viewMode: [ViewMode.Preview, ViewMode.Single].includes(viewMode)
            ? ViewMode.List
            : viewMode,
          direction: ViewMode.Single === viewMode ? FlowDirection.Row : FlowDirection.Column,
        })
        .display(page.ref);

      return () => {
        api.destroy();

        // we may not have a stage if the API is not in the viewport
        if (api.getStage()) {
          api.setStageSize({width: 0, height: 0});
        }
        ref.current = null;
      };
    }, [viewMode, gridCellMaxWidth]);

    const dispatchCommand = <T extends keyof CommandsList>(
      command: T,
      payload: CommandsList[T],
      callback?: Function
    ): Promise<any> => {
      return app.core
        .dispatch(command, payload)
        .then(res => {
          if (callback) {
            callback();
          }
        })
        .catch(error => {
          if (error instanceof ApplyPageStyleError) {
            ui.dispatch(
              UserInterfaceActions.setPageStyleErrorTip({open: true, errorString: error.message})
            );
          }
          console.error(error);
        });
    };

    const onDropNewPicture = () => {
      // Hide the autofill tip to avoid a clash of tips
      ui.dispatch(UserInterfaceActions.setTipOpen({name: 'useAutofill', open: false}));
      // Show the 'Apply a layout' tip
      ui.dispatch(
        UserInterfaceActions.setTipOpen({name: 'arrangePicturesByApplyingLayout', open: true})
      );
    };

    /**
     *
     * @param event
     */
    const onDragOver = (event: React.DragEvent<HTMLElement>, state: DragAndDropState): boolean => {
      let dragAllowed = true;
      const detection = getDetectionType(state.data);
      const {
        state: {canEditPageLayout},
      } = ui;

      // if we have an asset type, validate the type (User, ScrapBook & Mask assets supported), and store the event
      if (
        detection.assetType &&
        [
          AssetType.User,
          AssetType.ScrapbookImage,
          AssetType.ScrapbookClipping,
          AssetType.Mask,
        ].includes(detection.assetType)
      ) {
        // Masks can only be applied to a page item.
        if (AssetType.Mask === detection.assetType) {
          if (!ref?.current?.getSelectedItem()) {
            dragAllowed = false;
          }
        }

        dispatchCommand('PAGE.DRAG_OVER', {
          pageNumber: page.pagenumber,
          isSwapEvent: false,
          assetType: detection.assetType,
          event: event as unknown as DragEvent,
        }).catch(() => {});

        if (AssetType.User === detection.assetType) {
          dispatchCommand('PAGE.DRAG', {
            pageNumber: page.pagenumber,
            type: 0,
            event: event as unknown as DragEvent,
          }).catch(() => {});
        }
      }

      if (
        detection.detectionType &&
        [DetectionType.Background, DetectionType.PageStyle].includes(detection.detectionType)
      ) {
        dispatchCommand('PAGE.DRAG', {
          pageNumber: page.pagenumber,
          type: detection.detectionType,
          event: event as unknown as DragEvent,
        }).catch(() => {});

        const highlightedPage = ref.current?.getHighlightedPage();
        if (highlightedPage && highlightedPage <= PageDetectionActionType.None) {
          dragAllowed = false;
        }
      } else if (
        detection.detectionType &&
        [DetectionType.ScrapBookImage].includes(detection.detectionType)
      ) {
        let scrapBookPictureDropMode = canEditPageLayout
          ? layout.features.scrapBookPictureDropMode.nonFixed
          : layout.features.scrapBookPictureDropMode.fixed;

        if (experience.EDITOR.editorType.useGreetingCardEditor) {
          scrapBookPictureDropMode = ScrapBookDropMode.AlwaysCreateNew;
        }

        if (ScrapBookDropMode.ExistingPictureBoxOnly === scrapBookPictureDropMode) {
          dragAllowed = null !== ref?.current?.getSelectedItem();
        } else {
          dragAllowed = ScrapBookDropMode.None !== scrapBookPictureDropMode;
        }
      } else if (
        detection.detectionType &&
        [DetectionType.ScrapbookClipping].includes(detection.detectionType)
      ) {
        let scrapBookClippingDropMode = canEditPageLayout
          ? layout.features.scrapBookClippingDropMode.nonFixed
          : layout.features.scrapBookClippingDropMode.fixed;

        if (experience.EDITOR.editorType.useGreetingCardEditor) {
          scrapBookClippingDropMode = ScrapBookDropMode.AlwaysCreateNew;
        }

        dragAllowed = ScrapBookDropMode.AlwaysCreateNew === scrapBookClippingDropMode;
      } else if (detection.detectionType) {
        dragAllowed = null !== ref.current?.getSelectedItem();
      } else {
        if (!canEditPageLayout) {
          dragAllowed = null !== ref.current?.getSelectedItem();
        } else {
          dragAllowed = true;
        }
      }

      event.preventDefault();
      return dragAllowed;
    };

    const onDragLeave = (event: React.DragEvent<HTMLElement>, state: DragAndDropState): void => {
      const detection = getDetectionType(state.data);
      if (detection.detectionType) {
        dispatchCommand('PAGE.DRAG_LEAVE', {
          pageNumber: page.pagenumber,
          type: detection.detectionType,
          assetType: detection.assetType,
          isSwapEvent: false,
          event: event as unknown as DragEvent,
        }).catch(() => {});
      }
    };

    const onDrop = (event: React.DragEvent<HTMLElement>, state: DragAndDropState): void => {
      const payload = state.data;

      if (!payload) {
        return;
      }

      const {
        state: {canEditPageLayout},
      } = ui;
      const detection = getDetectionType(payload);

      if (DetectionType.None !== detection.detectionType) {
        dispatchCommand('PAGE.DROP', {
          type: detection.detectionType as DetectionType,
          pageNumber: page.pagenumber,
          payload: payload,
          event: event as unknown as DragEvent,
          pageLocked: !canEditPageLayout && !experience.EDITOR.editorType.useGreetingCardEditor,
        })
          .then(() => {
            if (
              DetectionType.User === detection.detectionType &&
              AssetType.User === (payload as any).assetType
            ) {
              onDropNewPicture();
            }
          })
          .catch(() => {});

        // fire a project level event to clear away any detection shapes
        app.core
          .dispatch('PROJECT.DRAG_END', {
            type: detection.detectionType,
          })
          .catch(error => {
            console.error(error);
          });
      }

      return;
    };

    const onDragEnter = (event: React.DragEvent<HTMLElement>, state: DragAndDropState) => {
      const detection = getDetectionType(state.data);
      if (DetectionType.None !== detection.detectionType) {
        dispatchCommand('PAGE.DRAG', {
          pageNumber: page.pagenumber,
          type: detection.detectionType,
          event: event as unknown as DragEvent,
        }).catch(() => {});
      }
    };

    const pageContainerClasses = classNames(
      'tpx-pageContainer',
      'focus-visible:outline',
      'focus-visible:outline-[12px]',
      'focus-visible:-outline-offset-1',
      'focus-visible:outline-themeFocusColour',
      uiLayoutMode !== 'basic' && [
        'relative',
        'flex',
        'self-center',
        'justify-center',
        'sm:snap-center',
        '[.single-page_&]:lg:snap-center', // Snap to the page rather than the LI when there is only one page
        'transition-transform',
        'shadow-[0_2px_5px_rgb(0_0_0_/_15%)]',
      ],
      // Append the instance's className
      className
    );

    const pageFooterClasses = classNames(
      'flex',
      'flex-col',
      'lg:min-h-xl',
      'content-center',

      // In single page view on small screen, the page name is absolutely positioned to allow the page to center vertically in the space
      storeViewMode === ViewMode.Single && ui.state.isSmallScreen
        ? ['absolute', 'w-full', 'top-full', 'pt-sm']
        : ['mt-sm', 'relative']
    );

    const canvasContainerClasses = classNames(
      'group-[.dragging]:z-[-1]',
      showPageHighlights && 'pageHighlights' // The component class to show the selected and hovered page side highlights (css class in index.css)
    );

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        // Move focus to selected (or first) tool palette button
        const paletteButton = document.querySelector('[data-palette-button][aria-selected="true"]');
        if (paletteButton) {
          (paletteButton as HTMLElement).focus();
        } else {
          const paletteButtons = document.querySelectorAll('[data-palette-button]');
          (paletteButtons[0] as HTMLElement)?.focus();
        }
      }
    };

    return (
      <>
        <div
          id={`pageContainer-${page.pagenumber}`}
          className={pageContainerClasses}
          ref={pageContainerRef}
          tabIndex={0}
          onKeyDown={onKeyDown}
          data-tpx-page
        >
          <Drop
            name={DragAndDropNames.PAGE}
            dropEffect="copy"
            dragEnter={onDragEnter}
            dragOver={onDragOver}
            dragLeave={onDragLeave}
            drop={onDrop}
          >
            <div ref={canvasRef} id={id} className={canvasContainerClasses}></div>
          </Drop>
        </div>
        {children && <div className={pageFooterClasses}>{children}</div>}
      </>
    );
  }
);

export default Page;
